import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Chip from "@material-ui/core/Chip";
import PropTypes from "prop-types";

const useStyles = makeStyles(() => ({
  chip: {
	borderColor: "#780F9E",
	color: "#780F9E",
	transition: "background-color 0.2s ease, color 0.2s ease",
	"&:hover": {
	  backgroundColor: "#780F9E !important",
	  color: "#FFF",
	},
	"&:focus, &:active": {
	  backgroundColor: "#780F9E !important",
	  color: "#FFF",
	},
  },
  selectedChip: {
	backgroundColor: "#780F9E !important",
	color: "#FFF",
	"&:hover, &:focus, &:active": {
	  backgroundColor: "#5E0C7A !important",
	  color: "#FFF",
	},
  },
}));

const ToggleChips = ({ chipData, subFilters, setSubFilters }) => {
  const classes = useStyles();

  // Determines if a chip should be selected based on subFilters
  const isChipSelected = (chipFilter, multiSelect) => {
	if (!chipFilter) return false;

	for (const key in chipFilter) {
	  const chipValue = chipFilter[key];
	  const subFilterValue = subFilters[key];

	  if (chipValue === null) {
		if (key in subFilters) return false;
	  } else if (multiSelect && Array.isArray(chipValue)) {
		// For multi-select, check if chip's values are all present in current filter
		if (!Array.isArray(subFilterValue)) return false;
		if (!chipValue.every((val) => subFilterValue.includes(val))) return false;
	  } else if (Array.isArray(chipValue)) {
		if (!Array.isArray(subFilterValue) || chipValue.length !== subFilterValue.length ||
			!chipValue.every((val) => subFilterValue.includes(val))) {
		  return false;
		}
	  } else if (subFilterValue !== chipValue) {
		return false;
	  }
	}
	return true;
  };
  
  

  const handleToggle = (chipKey) => {
	let newFilters = { ...subFilters };
	const chip = chipData.find((c) => c.key === chipKey);

	if (!chip) return;

	if (chip.multiSelect) {
	  // Multi-select: toggle individual values within the array
	  Object.keys(chip.filter).forEach((key) => {
		const chipValues = chip.filter[key];
		if (!Array.isArray(chipValues)) return;
		const currentValues = Array.isArray(newFilters[key]) ? [...newFilters[key]] : [];
		const allPresent = chipValues.every(v => currentValues.includes(v));
		if (allPresent) {
		  const remaining = currentValues.filter(v => !chipValues.includes(v));
		  if (remaining.length > 0) {
			newFilters[key] = remaining;
		  } else {
			delete newFilters[key];
		  }
		} else {
		  newFilters[key] = [...new Set([...currentValues, ...chipValues])];
		}
	  });
	} else if (isChipSelected(chip.filter)) {
	  Object.keys(chip.filter).forEach((key) => {
		if (newFilters[key] === chip.filter[key]) {
		  delete newFilters[key];
		}
	  });
	} else {
	  Object.assign(newFilters, chip.filter);
	}
	setSubFilters(newFilters, true);
  };
  

  return (
	<div
	  style={{
		padding: 16,
		display: "flex",
		gap: 8,
		flexWrap: "wrap",
		alignItems: "center",
	  }}
	>
	  <strong>Shortcuts: </strong>
	  {chipData.map((chip) => (
		<Chip
		  key={chip.key}
		  label={chip.label}
		  onClick={() => handleToggle(chip.key)}
		  onMouseDown={(e) => e.preventDefault()} // Prevents unwanted focus styling
		  className={isChipSelected(chip.filter, chip.multiSelect) ? classes.selectedChip : classes.chip}
		  data-selected={isChipSelected(chip.filter, chip.multiSelect) ? "true" : undefined}
		  variant="outlined"
		/>
	  ))}
	</div>
  );
};

ToggleChips.propTypes = {
  chipData: PropTypes.arrayOf(
	PropTypes.shape({
	  key: PropTypes.number.isRequired,
	  label: PropTypes.string.isRequired,
	  filter: PropTypes.object, // Each chip has an object filter
	})
  ).isRequired,
  subFilters: PropTypes.object.isRequired, // Key-value object for selected filters
  setSubFilters: PropTypes.func.isRequired, // Function to update selected filters
};

export default ToggleChips;
